import { useState } from "react"
import { FileText, Check, Eye, Download } from "lucide-react"
import { cn } from "@/lib/utils"

export interface CommercialDocumentCardProps {
  title: string
  fileName: string
  flow?: string
  status: string
  statusType?: "success" | "warning" | "error" | "default"
  date?: string
  documentUrl?: string | null
  onGetSignedUrl?: () => Promise<{ url: string } | string | { data?: { url: string } } | any>
}

export function CommercialDocumentCard({
  title,
  fileName,
  flow,
  status,
  statusType = "default",
  date,
  documentUrl,
  onGetSignedUrl,
}: CommercialDocumentCardProps) {
  const [downloading, setDownloading] = useState(false)

  const getActiveUrl = async (): Promise<string | null> => {
    if (onGetSignedUrl) {
      const res = await onGetSignedUrl()
      if (typeof res === "string") return res
      if (res?.url) return res.url
      if (res?.data?.url) return res.data.url
      return null
    }
    return documentUrl || null
  }

  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation()
    setDownloading(true)
    try {
      const activeUrl = await getActiveUrl()
      if (!activeUrl) return

      const response = await fetch(activeUrl)
      const blob = await response.blob()
      const blobUrl = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = blobUrl
      link.download = fileName || "document.pdf"
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(blobUrl)
    } catch (error) {
      console.error("Download fetch failed, opening link:", error)
      try {
        const activeUrl = await getActiveUrl()
        if (!activeUrl) return
        const link = document.createElement("a")
        link.href = activeUrl
        link.target = "_blank"
        link.download = fileName || "document.pdf"
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } catch (innerError) {
        console.error("Fallback download also failed:", innerError)
      }
    } finally {
      setDownloading(false)
    }
  }

  const handlePreview = async (e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      const activeUrl = await getActiveUrl()
      if (activeUrl) {
        window.open(activeUrl, "_blank", "noopener,noreferrer")
      }
    } catch (error) {
      console.error("Preview failed:", error)
    }
  }

  const fileExt = fileName.includes(".") ? fileName.slice(fileName.lastIndexOf(".") + 1).toUpperCase() : "PDF"
  const hasDocument = !!(documentUrl || onGetSignedUrl)

  return (
    <div className="group/card relative overflow-hidden flex flex-col justify-between rounded-xl border border-slate-200/80 bg-gradient-to-b from-white to-slate-50/50 p-3.5 shadow-xs hover:shadow-lg hover:shadow-primary/[0.06] hover:border-primary/35 transition-all duration-300 hover:-translate-y-0.5 min-h-[170px]">
      {/* Animated Top Accent Line */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-primary rounded-t-xl opacity-0 group-hover/card:opacity-100 transition-all duration-300" />

      {/* Main Content Area */}
      <div className="space-y-2.5">
        {/* Header: Title & Status */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="p-1.5 rounded-lg bg-primary/5 group-hover/card:bg-primary text-primary group-hover/card:text-primary-foreground border border-primary/15 group-hover/card:border-primary shrink-0 transition-all duration-300 shadow-2xs group-hover/card:scale-105">
              <FileText className="size-3.5" />
            </div>
            <h4 className="text-[11px] font-black uppercase tracking-wider text-slate-700 group-hover/card:text-primary transition-colors duration-200 truncate font-sans">
              {title}
            </h4>
          </div>

          <span className={cn(
            "text-[9px] font-extrabold uppercase tracking-wider px-2 py-0.5 rounded-full border flex items-center gap-1 shrink-0 shadow-2xs transition-all duration-200",
            statusType === "success" && "bg-emerald-50 text-emerald-700 border-emerald-200/80",
            statusType === "warning" && "bg-amber-50 text-amber-700 border-amber-200/80",
            statusType === "error" && "bg-red-50 text-red-700 border-red-200/80",
            statusType === "default" && "bg-slate-100 text-slate-600 border-slate-200"
          )}>
            {statusType === "success" && <span className="size-1.5 rounded-full bg-emerald-500 animate-pulse" />}
            {statusType === "warning" && <span className="size-1.5 rounded-full bg-amber-500 animate-pulse" />}
            {statusType === "error" && <span className="size-1.5 rounded-full bg-red-500" />}
            {status}
          </span>
        </div>

        {/* File & Flow Metadata Box */}
        <div className="bg-slate-50/90 group-hover/card:bg-white rounded-lg p-2.5 border border-slate-100 group-hover/card:border-slate-200/80 transition-all duration-300 space-y-1 shadow-2xs">
          <div className="flex items-center justify-between gap-2">
            <p className="text-[11px] font-bold text-slate-800 truncate font-mono tracking-tight" title={fileName}>
              {fileName}
            </p>
            <span className="text-[8.5px] font-extrabold px-1 py-0.5 rounded bg-primary/10 text-primary font-mono shrink-0 uppercase tracking-tighter">
              {fileExt}
            </span>
          </div>
          {flow && (
            <p className="text-[9.5px] font-semibold text-slate-400 flex items-center gap-1 uppercase tracking-tight">
              <span>{flow}</span>
            </p>
          )}
        </div>

        {/* Issue Date */}
        {date && (
          <div className="flex items-center justify-between text-[10px] text-slate-500 pt-0.5">
            <span className="text-[9.5px] text-slate-400 uppercase font-bold tracking-tight">Created</span>
            <span className="font-semibold text-slate-700 font-mono">{date}</span>
          </div>
        )}
      </div>

      {/* Footer Section */}
      <div className="pt-2 border-t border-slate-100 space-y-1.5">
        {hasDocument ? (
          <div className="grid grid-cols-2 gap-1.5">
            <button
              type="button"
              onClick={handlePreview}
              className="group/btn flex items-center justify-center gap-1.5 py-1.5 px-2.5 rounded-lg border border-primary/20 bg-primary/5 hover:bg-primary text-primary hover:text-primary-foreground text-[10px] font-extrabold transition-all duration-200 active:scale-95 cursor-pointer shadow-2xs text-center"
              title="Preview Document"
            >
              <Eye className="size-3 shrink-0 transition-transform duration-200 group-hover/btn:scale-110" />
              <span className="truncate">Preview</span>
            </button>
            <button
              type="button"
              onClick={handleDownload}
              disabled={downloading}
              className="group/btn flex items-center justify-center gap-1.5 py-1.5 px-2.5 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-800 hover:border-slate-800 text-slate-700 hover:text-white text-[10px] font-extrabold transition-all duration-200 active:scale-95 cursor-pointer shadow-2xs disabled:opacity-50"
              title="Download Document"
            >
              <Download className={cn("size-3 shrink-0 transition-transform duration-200 group-hover/btn:translate-y-0.5", downloading && "animate-bounce")} />
              <span className="truncate">{downloading ? "Downloading..." : "Download"}</span>
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-center py-1 text-[10px] text-slate-400 italic bg-slate-50/50 rounded-lg border border-dashed border-slate-200">
            Document pending generation
          </div>
        )}
      </div>
    </div>
  )
}
